import { useState } from 'react';
import { Link } from 'react-router';
import { Avatar } from '../components/Avatar';
import { CategoryChip, CertificationChip } from '../components/Chips';
import { FilterChip } from '../components/FilterChip';
import { EmptyBlock, ErrorBlock, LoadingBlock, PageHeader } from '../components/states';
import { formatLongMonthDay } from '../lib/datetime';
import { roleLine } from '../lib/people';
import { useDirectory, useEvent, type DirectoryPerson } from '../lib/queries';
import { useDebounced } from '../lib/useDebounced';

/**
 * Who is coming (module 6). The filters are the ones these attendees actually use to find each
 * other — industry and certification (DIR-02) — and the card carries nothing more than DIR-01
 * asks for: no email, no phone, at any setting (PRF-05).
 */
export function DirectoryPage() {
  const event = useEvent();
  const [search, setSearch] = useState('');
  const [certificationId, setCertificationId] = useState('');
  const [industryId, setIndustryId] = useState('');
  const q = useDebounced(search, 250);

  const directory = useDirectory({ q, certificationId, industryId });
  const pages = directory.data?.pages ?? [];
  const first = pages.at(0);
  const people = pages.flatMap((page) => page.people);
  const filtered = Boolean(q || certificationId || industryId);

  return (
    <>
      {/* The search box and the filters stay put while the list moves under them: on a list of a
          few hundred people, scrolling back up to change a filter is the whole interaction. */}
      <div className="sticky top-0 z-10 border-b border-border-2 bg-surface">
        <PageHeader title="Who's coming" />

        <div className="px-gutter space-y-2.5 pb-3">
          <input
            type="search"
            value={search}
            onChange={(item) => setSearch(item.target.value)}
            placeholder="Search name, company or industry"
            aria-label="Search the directory"
            className="text-body rounded-button min-h-touch w-full border border-border-input bg-surface px-4 text-ink placeholder:text-ink-4"
          />

          {first && first.certifications.length > 0 && (
            <div className="-mx-gutter px-gutter flex gap-2 overflow-x-auto pb-1">
              <FilterChip active={!certificationId} onClick={() => setCertificationId('')}>
                Any certification
              </FilterChip>
              {first.certifications.map((certification) => (
                <FilterChip
                  key={certification.id}
                  active={certification.id === certificationId}
                  onClick={() =>
                    setCertificationId(certification.id === certificationId ? '' : certification.id)
                  }
                >
                  {certification.label}
                </FilterChip>
              ))}
            </div>
          )}

          {first && first.industries.length > 0 && (
            <select
              value={industryId}
              onChange={(item) => setIndustryId(item.target.value)}
              aria-label="Filter by industry"
              className="text-body rounded-button min-h-touch w-full border border-border-input bg-surface px-4 text-ink"
            >
              <option value="">All industries</option>
              {first.industries.map((industry) => (
                <option key={industry.id} value={industry.id}>
                  {industry.label}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      <main className="px-gutter pt-3 pb-6">
        {directory.isPending && <LoadingBlock label="Loading the directory…" />}

        {directory.isError && (
          <ErrorBlock
            message="The directory could not be loaded."
            onRetry={() => void directory.refetch()}
          />
        )}

        {first && (
          <>
            {/* People, never tickets (DATA-01) — and the second number says plainly that the
                list is not everybody yet, which is the question this screen provokes. */}
            <p className="text-subtitle text-ink-3">
              {countLabel(first.matched)} · {first.registered} registered so far
            </p>

            {people.length === 0 && filtered && (
              <EmptyBlock title="Nothing matches that">
                Try a different name, or clear the industry and certification filters.
              </EmptyBlock>
            )}

            {people.length === 0 && !filtered && (
              <EmptyBlock title="Nobody is listed yet">
                The directory fills up as people register and fill in their profiles.
              </EmptyBlock>
            )}

            <div className="space-y-card mt-3">
              {people.map((person) => (
                <PersonCard key={person.id} person={person} />
              ))}
            </div>

            {directory.hasNextPage && (
              <button
                type="button"
                onClick={() => void directory.fetchNextPage()}
                disabled={directory.isFetchingNextPage}
                className="text-row rounded-button min-h-touch mt-4 w-full border border-border-input bg-surface font-semibold text-ink disabled:text-ink-4"
              >
                {directory.isFetchingNextPage ? 'Loading…' : 'Show more'}
              </button>
            )}

            {first.registrationClosesOn && event.data && (
              <p className="text-subtitle mt-5 text-ink-4">
                Registration runs until{' '}
                {formatLongMonthDay(first.registrationClosesOn, event.data.timezone)}, so this list
                keeps growing.
              </p>
            )}
          </>
        )}
      </main>
    </>
  );
}

function PersonCard({ person }: { person: DirectoryPerson }) {
  // DATA-06: most of these arrive blank for somebody, and a card of empty lines is not an error.
  const role = roleLine(person.jobTitle, person.company);

  return (
    // The card is the way to the profile (DIR-05). The mockup draws no chevron, and it does not
    // need one: on a list where every row opens, the affordance is the card.
    <Link
      to={`/directory/${person.id}`}
      className="rounded-card flex gap-3 border border-border bg-surface p-4"
    >
      <Avatar name={person.name} photoAssetId={person.photoAssetId} />

      <div className="min-w-0 flex-1">
        <h2 className="text-row font-semibold text-ink">{person.name}</h2>
        {role && <p className="text-subtitle mt-0.5 text-ink-3">{role}</p>}
        {person.industry && <p className="text-subtitle text-ink-3">{person.industry}</p>}

        <div className="mt-2 flex flex-wrap gap-1.5">
          <CategoryChip label={person.category} />
          {person.certifications.map((certification) => (
            <CertificationChip key={certification.id} label={certification.label} />
          ))}
        </div>

        {/* DIR-04: she is here on her registration answers, which is why there is no industry. */}
        {!person.hasProfile && (
          <p className="text-eyebrow mt-2 text-ink-4">Profile not filled in yet</p>
        )}
      </div>
    </Link>
  );
}

function countLabel(matched: number): string {
  return matched === 1 ? '1 attendee' : `${matched} attendees`;
}
